<template>
  <span>
    <button v-if="!sansBouton" type="button" class="btn btn-outline-success" :class="taille" @click="ouvrir" :title="titre">
      <i class="bi bi-phone me-1"></i>{{ libelle }}
    </button>

    <teleport to="body">
      <div v-if="ouvert" class="pel-fond" @click.self="fermer">
        <div class="pel-carte card shadow">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0"><i class="bi bi-phone me-2"></i>Paiement en ligne</h5>
            <button class="btn-close" @click="fermer"></button>
          </div>

          <div class="card-body">
            <div v-if="chargement" class="text-center my-4"><div class="spinner-border text-success"></div></div>

            <!-- Préparation -->
            <template v-else-if="!tx">
              <div v-if="!info.compteConfigure" class="alert alert-warning mb-0">
                Le paiement en ligne n'est pas encore configuré pour votre clinique.
                Un administrateur peut l'activer dans <strong>Administration → Paiement en ligne</strong>.
              </div>
              <template v-else>
                <p class="mb-2">{{ info.libelle }} — reste à payer : <strong>{{ fcfa(info.reste) }}</strong></p>
                <div class="row g-2">
                  <div class="col-sm-6" v-if="objetType !== 'CONSULTATION'">
                    <label class="form-label small mb-1">Montant à payer (FCFA)</label>
                    <input v-model.number="montant" type="number" min="5" step="5" :max="info.reste" class="form-control">
                  </div>
                  <div class="col-sm-6">
                    <label class="form-label small mb-1">Téléphone du patient</label>
                    <input v-model="telephone" class="form-control" placeholder="Numéro de téléphone du patient">
                  </div>
                </div>
                <small class="text-muted d-block mt-2">Le patient paie par mobile money ou par carte bancaire. L'argent arrive sur le compte de la clinique et le paiement s'enregistre tout seul dans la caisse.</small>
                <div v-if="info.simulateur" class="alert alert-info small mt-2 mb-0 py-2">Mode test : simulateur, aucun argent réel.</div>
              </template>
              <div v-if="erreur" class="alert alert-danger mt-3 mb-0 py-2">{{ erreur }}</div>
            </template>

            <!-- Lien envoyé -->
            <template v-else>
              <div class="text-center">
                <div v-if="tx.statut === 'ACCEPTEE'" class="py-3">
                  <i class="bi bi-check-circle-fill text-success" style="font-size: 3rem"></i>
                  <h5 class="mt-2">Paiement reçu : {{ fcfa(tx.montant) }}</h5>
                  <p class="text-muted mb-0" v-if="tx.encaissementReference">Enregistré dans la caisse ({{ tx.operateur || 'en ligne' }}).</p>
                  <p class="text-danger mb-0" v-else>{{ tx.message || "Payé mais pas encore enregistré en caisse." }}</p>
                </div>
                <div v-else-if="tx.statut === 'REFUSEE' || tx.statut === 'EXPIREE'" class="py-3">
                  <i class="bi bi-x-circle-fill text-danger" style="font-size: 3rem"></i>
                  <h5 class="mt-2">Paiement {{ tx.statut === 'REFUSEE' ? 'refusé' : 'expiré' }}</h5>
                  <p class="text-muted">{{ tx.message }}</p>
                  <button class="btn btn-outline-primary" @click="tx = null">Nouvelle demande</button>
                </div>
                <template v-else>
                  <p class="mb-2">Le patient scanne ce code avec son téléphone, ou ouvre le lien envoyé :</p>
                  <img v-if="qr" :src="qr" alt="QR code de paiement" class="pel-qr">
                  <div class="input-group input-group-sm my-2">
                    <input class="form-control" :value="tx.urlPaiement" readonly>
                    <button class="btn btn-outline-secondary" @click="copier"><i class="bi bi-clipboard"></i></button>
                  </div>
                  <div class="d-flex justify-content-center gap-2 flex-wrap">
                    <a class="btn btn-success btn-sm" :href="lienWhatsapp" target="_blank" rel="noopener"><i class="bi bi-whatsapp me-1"></i>Envoyer par WhatsApp</a>
                    <a class="btn btn-outline-primary btn-sm" :href="tx.urlPaiement" target="_blank" rel="noopener"><i class="bi bi-box-arrow-up-right me-1"></i>Ouvrir ici</a>
                  </div>
                  <div class="mt-3 text-muted small"><span class="spinner-grow spinner-grow-sm text-warning me-1"></span>En attente du paiement de {{ fcfa(tx.montant) }}…</div>
                </template>
              </div>
            </template>
          </div>

          <div class="card-footer text-end">
            <button class="btn btn-secondary me-2" @click="fermer">Fermer</button>
            <button v-if="!tx && info.compteConfigure" class="btn btn-success" :disabled="envoi" @click="demander">
              {{ envoi ? 'Préparation…' : 'Créer le lien de paiement' }}
            </button>
            <button v-if="tx && !termine" class="btn btn-outline-primary" @click="verifier">Vérifier maintenant</button>
          </div>
        </div>
      </div>
    </teleport>
  </span>
</template>

<script>
import api, { messageErreur, notifier } from '@/api'
import QRCode from 'qrcode'

export default {
  name: 'DemandePaiementEnLigne',
  props: {
    objetType: { type: String, required: true },
    objetId: { type: [Number, String], required: true },
    libelle: { type: String, default: 'Payer en ligne' },
    taille: { type: String, default: 'btn-sm' },
    /** Pas de bouton : la fenêtre est ouverte par le parent (ref.ouvrir()). */
    sansBouton: { type: Boolean, default: false }
  },
  emits: ['paye'],
  data() {
    return { ouvert: false, chargement: false, envoi: false, erreur: '', info: {}, montant: null, telephone: '', tx: null, qr: null, minuteur: null }
  },
  computed: {
    titre() {
      return 'Mobile money ou carte bancaire, sur le compte de la clinique'
    },
    termine() {
      return this.tx && ['ACCEPTEE', 'REFUSEE', 'EXPIREE'].includes(this.tx.statut)
    },
    lienWhatsapp() {
      const tel = (this.tx?.telephone || '').replace(/[^0-9]/g, '')
      const numero = tel.length === 8 ? '223' + tel : tel
      const texte = `Bonjour, voici le lien pour régler ${this.fcfa(this.tx?.montant)} (${this.tx?.objetLibelle}) : ${this.tx?.urlPaiement}`
      return `https://wa.me/${numero}?text=${encodeURIComponent(texte)}`
    }
  },
  beforeUnmount() {
    this.arreter()
  },
  methods: {
    async ouvrir() {
      this.ouvert = true
      this.erreur = ''
      this.tx = null
      this.chargement = true
      try {
        this.info = (await api.custom(`paiements-en-ligne/reste-du?objetType=${this.objetType}&objetId=${this.objetId}`, 'GET')).data
        this.montant = this.info.reste
        this.telephone = this.info.telephone || ''
      } catch (e) {
        this.erreur = messageErreur(e)
        this.info = { compteConfigure: true, reste: 0 }
      } finally {
        this.chargement = false
      }
    },
    async demander() {
      this.envoi = true
      this.erreur = ''
      try {
        this.tx = (await api.post('paiements-en-ligne', {
          objetType: this.objetType, objetId: Number(this.objetId), montant: this.montant, telephone: this.telephone
        })).data
        this.qr = await QRCode.toDataURL(this.tx.urlPaiement, { width: 220, margin: 1 })
        this.suivre()
      } catch (e) {
        this.erreur = messageErreur(e)
      } finally {
        this.envoi = false
      }
    },
    suivre() {
      this.arreter()
      this.minuteur = setInterval(() => this.rafraichir(), 4000)
    },
    arreter() {
      if (this.minuteur) clearInterval(this.minuteur)
      this.minuteur = null
    },
    async rafraichir() {
      try {
        // Toutes les 12 s, on interroge aussi l'opérateur (utile si sa notification ne nous parvient pas)
        this.tours = (this.tours || 0) + 1
        this.tx = this.tours % 3 === 0
          ? (await api.post(`paiements-en-ligne/${this.tx.reference}/verifier`, {})).data
          : (await api.custom(`paiements-en-ligne/${this.tx.reference}`, 'GET')).data
        if (this.termine) {
          this.arreter()
          if (this.tx.statut === 'ACCEPTEE') {
            notifier(`Paiement en ligne reçu : ${this.fcfa(this.tx.montant)}`, 'success')
            this.$emit('paye', this.tx)
          }
        }
      } catch (e) {
        this.arreter()
      }
    },
    async verifier() {
      try {
        this.tx = (await api.post(`paiements-en-ligne/${this.tx.reference}/verifier`, {})).data
        if (this.termine) {
          this.arreter()
          if (this.tx.statut === 'ACCEPTEE') this.$emit('paye', this.tx)
        }
      } catch (e) {
        notifier(messageErreur(e))
      }
    },
    copier() {
      navigator.clipboard?.writeText(this.tx.urlPaiement)
      notifier('Lien copié.', 'success')
    },
    fermer() {
      this.arreter()
      this.ouvert = false
    },
    fcfa(v) {
      return new Intl.NumberFormat('fr-FR').format(v || 0) + ' FCFA'
    }
  }
}
</script>

<style scoped>
.pel-fond { position: fixed; inset: 0; background: rgba(0, 0, 0, .45); z-index: 1060; display: flex; align-items: flex-start; justify-content: center; padding: 40px 16px; overflow-y: auto; }
.pel-carte { width: 100%; max-width: 480px; border-radius: 12px; }
.pel-qr { width: 220px; height: 220px; }
</style>
